import { PermissionCategoryResponse } from '../../../apollo/queries/v3/RolesNPermissions/permissionCategories.queries';
import { AssignedPermissions } from '../CreateNewRole/PermissionCategoryContext';
import { PermissionCategory } from '../CreateNewRole/models/PermissionCategoryViewModel';

export const normalizePermission = (value: string): string =>
  value
    .trim()
    .replace(/^["']|["']$/g, '')
    .replace(/,+$/, '')
    .trim();

export const toPermissionCategoryResponses = (
  categories: PermissionCategory[],
): PermissionCategoryResponse[] =>
  categories.map((category) => ({
    id: category.id,
    name: category.name,
    permissions: category.permissions.map((permission) => ({
      permission: permission.permission,
      dependsOn: permission.dependsOn || [],
    })),
  }));

export const buildPermissionDependencyMap = (
  categories: PermissionCategoryResponse[],
): Map<string, string[]> => {
  const dependencyMap = new Map<string, string[]>();

  categories.forEach((category) => {
    category.permissions.forEach((permission) => {
      dependencyMap.set(
        permission.permission,
        permission.dependsOn.map((dependency) =>
          normalizePermission(dependency),
        ),
      );
    });
  });

  return dependencyMap;
};

export const resolvePermissionDependencies = (
  permissions: string[],
  categories: PermissionCategoryResponse[],
): string[] => {
  const dependencyMap = buildPermissionDependencyMap(categories);
  const resolved: string[] = [];
  const seen = new Set<string>();

  const addWithDependencies = (permission: string) => {
    const normalizedPermission = normalizePermission(permission);
    if (!normalizedPermission || seen.has(normalizedPermission)) {
      return;
    }

    const dependencies = dependencyMap.get(normalizedPermission) || [];
    dependencies.forEach(addWithDependencies);
    seen.add(normalizedPermission);
    resolved.push(normalizedPermission);
  };

  permissions.forEach(addWithDependencies);
  return resolved;
};

const getPermissionModuleFallback = (permission: string): string => {
  const [, resource = ''] = permission.split(':');
  const segment = resource.split('.')[0];

  return segment
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const mapPermissionsToAssigned = (
  permissions: string[],
  categories: PermissionCategory[],
): AssignedPermissions[] =>
  permissions.map((permission) => {
    const category = categories.find((item) =>
      item.permissions.some((entry) => entry.permission === permission),
    );

    return {
      permission,
      module: category?.name || getPermissionModuleFallback(permission),
    };
  });
